import React from 'react';
import TermsAndConditions from 'components/common/form/custom/TermsAndConditions';
import TextWithCompanyName from 'components/common/form/custom/TextWithCompanyName';
import UsersBlock from 'components/common/form/custom/AuthorizedUsers/UsersBlock';
import InfoCardBlock from 'components/common/form/custom/ReplicatedInfo/InfoCardBlock';
import { WebFormCustomFields } from 'store/ui-stores/web-form/webFormStore';
import {
  DDIAAffiliateSubsidiary,
  DDIAAnotherContract,
  DDIANotActivelyAtWorkEE,
  ReplicatedCustomInfo,
} from 'types/formService';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { InputText } from '@insurancemenu/imenu-components-lib';
import { createStyles } from 'components/common/form/custom/ReplicatedInfo/InfoCardModal/styles';
import {
  validateDateOfBirth,
  validateDateThatIsAfter,
  validateDateThatNotInFuture,
} from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import PlanRateCap from 'components/common/form/custom/PlanRateCap';
import {
  getCompensationEEBlock,
  getExemptEEBlock,
} from 'components/common/form/custom/carrier-components/MemorialHermann';
import { getDoctors, getMedications } from 'components/common/form/custom/carrier-components/BravoHealth';
import { getAdditionalContacts } from 'components/common/form/custom/carrier-components/DDAR';

const asSubsidiary = (info: ReplicatedCustomInfo): DDIAAffiliateSubsidiary => info as DDIAAffiliateSubsidiary;
const asContract = (info: ReplicatedCustomInfo): DDIAAnotherContract => info as DDIAAnotherContract;
const asInactiveEE = (info: ReplicatedCustomInfo): DDIANotActivelyAtWorkEE => info as DDIANotActivelyAtWorkEE;

export const WEB_FORM_CUSTOM_COMPONENTS = Object.freeze({
  termsAndConditions: <TermsAndConditions key="termsAndConditions" />,
  textWithCompanyName: <TextWithCompanyName key="textWithCompanyName" />,
  authorizedUsers: <UsersBlock key="authorizedUsers" />,
  rateCaps: <PlanRateCap key="rateCaps" />,
  affiliateSubsidiary: (
    <InfoCardBlock
      key="affiliateSubsidiary"
      componentName="affiliateSubsidiary"
      amountOfAdditionalCards={6}
      customModalHeaderName="Authorized User"
      addButtonName="Add An Affiliate/subsidiary"
      getCustomComponentField={(customField: WebFormCustomFields) => {
        return customField.affiliateSubsidiary;
      }}
      columns={[
        { title: 'Name', width: '13rem', getValue: (info): string => asSubsidiary(info).name ?? '' },
        { title: 'Address', width: '13rem', getValue: (info): string => asSubsidiary(info).address ?? '' },
        {
          title: 'Business',
          width: '13rem',
          getValue: (info): string => asSubsidiary(info).natureOfBusiness ?? '',
        },
        {
          title: 'Owned %',
          width: '11rem',
          getValue: (info): string => asSubsidiary(info).ownedByApplicant ?? '',
        },
      ]}
      createCustomForm={(): FormController<DefaultFormFields> => {
        return new FormController<DefaultFormFields>({
          name: new FormControl<string>('', { validations: ['validName'], trim: true }),
          address: new FormControl<string>('', { validations: ['printableCharacters'], optional: true, trim: true }),
          natureOfBusiness: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
          ownedByApplicant: new FormControl<string>('', {
            validations: ['containsOnlyNumbers'],
            trim: true,
            customValidations: [
              {
                // eslint-disable-next-line prefer-regex-literals
                check: (value: string): boolean => new RegExp(/^[0-9][0-9]?$|^100$/).test(value),
                msg: 'Input should be from 0 to 100.',
              },
            ],
          }),
        });
      }}
      customModalContent={(customForm: FormController<DefaultFormFields>): JSX.Element => {
        const classes = createStyles();
        const name = customForm.fields.name;
        const address = customForm.fields.address;
        const natureOfBusiness = customForm.fields.natureOfBusiness;
        const ownedByApplicant = customForm.fields.ownedByApplicant;
        return (
          <>
            <InputText
              label="Name"
              name="name"
              maxLength={100}
              value={name.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={name.unpassedValidationMsg || ''}
              validationPassed={!name.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Address (if different)"
              name="address"
              maxLength={100}
              value={address.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={address.unpassedValidationMsg || ''}
              validationPassed={!address.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Nature of Business Operations"
              name="natureOfBusiness"
              maxLength={100}
              value={natureOfBusiness.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={natureOfBusiness.unpassedValidationMsg || ''}
              validationPassed={!natureOfBusiness.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="% Owned by Applicant"
              name="ownedByApplicant"
              maxLength={3}
              value={ownedByApplicant.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={ownedByApplicant.unpassedValidationMsg || ''}
              validationPassed={!ownedByApplicant.unpassedValidationMsg}
              className={classes.regularField}
            />
          </>
        );
      }}
    />
  ),
  anotherContract: (
    <InfoCardBlock
      key="anotherContract"
      componentName="anotherContract"
      amountOfAdditionalCards={3}
      customModalHeaderName="Authorized User"
      addButtonName="Add Another Contract"
      getCustomComponentField={(customField: WebFormCustomFields) => {
        return customField.anotherContract;
      }}
      columns={[
        {
          title: 'Coverage(s)',
          width: '15rem',
          getValue: (info): string => asContract(info).coverage ?? '',
        },
        {
          title: 'Carrier',
          width: '15rem',
          getValue: (info): string => asContract(info).carrier ?? '',
        },
        {
          title: 'Termination Date',
          width: '10rem',
          getValue: (info): string => normDateToShortAmericanDate(asContract(info).terminationDate),
        },
      ]}
      createCustomForm={(): FormController<DefaultFormFields> => {
        return new FormController<DefaultFormFields>({
          coverage: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
          carrier: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
          terminationDate: new FormControl<string>('', {
            validations: ['printableCharacters'],
            customValidations: [
              {
                check: (value: string): boolean => !validateDateThatNotInFuture(value),
                msg: 'Date cannot be in the past.',
              },
              {
                check: (value: string): boolean => !validateDateThatIsAfter(value, '2050-01-01'), // validation checks that picked date is earlier than 2050
                msg: 'Incorrect date picked',
              },
            ],
          }),
        });
      }}
      customModalContent={(customForm: FormController<DefaultFormFields>): JSX.Element => {
        const classes = createStyles();
        const coverage = customForm.fields.coverage;
        const carrier = customForm.fields.carrier;
        const terminationDate = customForm.fields.terminationDate;
        return (
          <>
            <InputText
              label="Coverage(s)"
              name="coverage"
              maxLength={100}
              value={coverage.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={coverage.unpassedValidationMsg || ''}
              validationPassed={!coverage.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Carrier"
              name="carrier"
              maxLength={100}
              value={carrier.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={carrier.unpassedValidationMsg || ''}
              validationPassed={!carrier.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Termination Date"
              name="terminationDate"
              htmlType="date"
              value={terminationDate.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={terminationDate.unpassedValidationMsg || ''}
              validationPassed={!terminationDate.unpassedValidationMsg}
              className={classes.regularField}
            />
          </>
        );
      }}
    />
  ),
  notActivelyAtWorkEE: (
    <InfoCardBlock
      key="notActivelyAtWorkEE"
      componentName="notActivelyAtWorkEE"
      amountOfAdditionalCards={16}
      customModalHeaderName="Employee"
      addButtonName="Add Another Employee"
      getCustomComponentField={(customField: WebFormCustomFields) => {
        return customField.notActivelyAtWorkEE;
      }}
      columns={[
        { title: 'Employee Name', width: '12rem', getValue: (info): string => asInactiveEE(info).fullName ?? '' },
        {
          title: 'DOB',
          width: '11rem',
          getValue: (info): string => normDateToShortAmericanDate(asInactiveEE(info).dob),
        },
        {
          title: 'Coverage Amount',
          width: '12rem',
          getValue: (info): string => asInactiveEE(info).coverageAmount ?? '',
        },
        { title: 'Reason', width: '15rem', getValue: (info): string => asInactiveEE(info).reason ?? '' },
        {
          title: 'Last Day Return Date',
          width: '11rem',
          getValue: (info): string => {
            const e = asInactiveEE(info);
            return `${normDateToShortAmericanDate(e.lastAtWorkDate)} ${normDateToShortAmericanDate(e.returnDate)}`;
          },
        },
      ]}
      createCustomForm={(): FormController<DefaultFormFields> => {
        return new FormController<DefaultFormFields>({
          fullName: new FormControl<string>('', { validations: ['validName'], trim: true }),
          dob: new FormControl<string>('', {
            validations: ['printableCharacters'],
            customValidations: [
              {
                check: (value: string): boolean => validateDateOfBirth(value, 120),
                msg: 'Incorrect date picked',
              },
              {
                check: (value: string): boolean =>
                  !validateDateThatIsAfter(value, `${new Date().getFullYear() - 13}-01-01`), // validation checks that EE is older than 14
                msg: 'Incorrect date picked',
              },
            ],
          }),
          coverageAmount: new FormControl<string>('', {
            validations: ['printableCharacters'],
            trim: true,
            customValidations: [
              {
                check: (value: string): boolean =>
                  // eslint-disable-next-line prefer-regex-literals
                  new RegExp(/^(300000(.00)?|(([1-9]\d{4}|[1-2]\d{5})(\.\d{2})?))$/).test(value),
                msg: 'From 10000 to 300000. Valid format is one of the following ####.## or #####',
              },
            ],
          }),
          lastAtWorkDate: new FormControl<string>('', {
            validations: ['printableCharacters'],
            customValidations: [
              {
                check: (value: string): boolean => validateDateThatNotInFuture(value),
                msg: 'Date cannot be in the future.',
              },
              {
                check: (value: string): boolean => validateDateOfBirth(value, 25), // validation checks that input is not older than
                msg: 'Incorrect date picked',
              },
            ],
          }),
          reason: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
          returnDate: new FormControl<string>('', {
            validations: ['printableCharacters'],
            customValidations: [
              {
                check: (value: string): boolean => !validateDateThatNotInFuture(value),
                msg: 'Date cannot be in the past.',
              },
              {
                check: (value: string): boolean => !validateDateThatIsAfter(value, '2050-01-01'), // validation checks that picked date is earlier than 2050
                msg: 'Incorrect date picked',
              },
            ],
          }),
        });
      }}
      customModalContent={(customForm: FormController<DefaultFormFields>): JSX.Element => {
        const classes = createStyles();
        const fullName = customForm.fields.fullName;
        const dob = customForm.fields.dob;
        const coverageAmount = customForm.fields.coverageAmount;
        const lastAtWorkDate = customForm.fields.lastAtWorkDate;
        const reason = customForm.fields.reason;
        const returnDate = customForm.fields.returnDate;
        return (
          <>
            <InputText
              label="Employee Name"
              name="fullName"
              maxLength={100}
              value={fullName.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={fullName.unpassedValidationMsg || ''}
              validationPassed={!fullName.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Date of Birth"
              name="dob"
              htmlType="date"
              value={dob.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={dob.unpassedValidationMsg || ''}
              validationPassed={!dob.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Coverage Amount"
              name="coverageAmount"
              maxLength={100}
              value={coverageAmount.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={coverageAmount.unpassedValidationMsg || ''}
              validationPassed={!coverageAmount.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Last Day Actively at Work"
              name="lastAtWorkDate"
              htmlType="date"
              value={lastAtWorkDate.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={lastAtWorkDate.unpassedValidationMsg || ''}
              validationPassed={!lastAtWorkDate.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Reason Not Actively at Work"
              name="reason"
              maxLength={100}
              value={reason.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={reason.unpassedValidationMsg || ''}
              validationPassed={!reason.unpassedValidationMsg}
              className={classes.regularField}
            />
            <InputText
              label="Expected Return to Work Date"
              name="returnDate"
              htmlType="date"
              value={returnDate.value?.toString()}
              onChange={customForm.onFieldChange}
              validationMessage={returnDate.unpassedValidationMsg || ''}
              validationPassed={!returnDate.unpassedValidationMsg}
              className={classes.regularField}
            />
          </>
        );
      }}
    />
  ),
  memorialHermannExemptEE: getExemptEEBlock(),
  memorialHermannCompensationEE: getCompensationEEBlock(),
  bravoHealthDoctor: getDoctors(),
  bravoHealthMedication: getMedications(),
  ddarAdditionalContacts: getAdditionalContacts(),
});
